import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sendToAiSupportSession, getDetailSession } from 'redux/actions/mvp/session.action';
import './create-session.scss';
import '@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css';
import { I18n } from '../../../../helpers/i18n';
import micphone from '../../../../static/svg/micphone.svg';
import arrowBackIcon from '../../../../static/svg/back.svg';
import ReactMarkdown from 'react-markdown';
import Webcam from 'react-webcam';
import {
  MVP_DEVICE_COMBOS,
  MVP_DEVICE_COMBO_I18N_KEYS,
  getDeviceComboStorageKey,
} from '../../../../constants/mvpDeviceCombos';

function AiSupportSessionVideo({ user, sessionInfo, joinSession, goBack, getDetailSession }) {
  const [content, setContent] = useState(false);
  const [paragraphs, setParagraphs] = useState([]);
  const [disable, setDisable] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [devices, setDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [hasCamera, setHasCamera] = useState(false);
  const [error, setError] = useState(null);
  const [confirmedDeviceCombo, setConfirmedDeviceCombo] = useState(null);
  const [isEditingDeviceCombo, setIsEditingDeviceCombo] = useState(false);

  const recognitionRef = useRef(null);
  const isRecognitionRunningRef = useRef(false);
  const silenceTimeoutRef = useRef(null);
  const speechToTextRef = useRef('');
  const conversationIdRef = useRef('');
  const parentMessageIdRef = useRef('');
  const paragraphsRef = useRef([]);
  const webcamRef = useRef(null);
  const confirmedDeviceComboRef = useRef(null);

  const SILENCE_TIMEOUT = 3000;

  const sessionType = sessionInfo?.sessionType || null;
  const sessionStorageKey = useMemo(
    () => getDeviceComboStorageKey(sessionInfo?._id || sessionInfo?.sessionUserId),
    [sessionInfo?._id, sessionInfo?.sessionUserId],
  );

  useEffect(() => {
    confirmedDeviceComboRef.current = confirmedDeviceCombo;
  }, [confirmedDeviceCombo]);

  useEffect(() => {
    setIsEditingDeviceCombo(false);

    if (!sessionStorageKey) {
      setConfirmedDeviceCombo(null);
      return;
    }

    try {
      const stored = sessionStorage.getItem(sessionStorageKey);
      if (stored && MVP_DEVICE_COMBOS.includes(stored)) {
        setConfirmedDeviceCombo(stored);
      } else {
        setConfirmedDeviceCombo(null);
      }
    } catch {
      setConfirmedDeviceCombo(null);
    }
  }, [sessionStorageKey]);

  const clearSilenceTracking = () => {
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
  };

  const stopRecognition = () => {
    if (recognitionRef.current && isRecognitionRunningRef.current) {
      recognitionRef.current.stop();
      isRecognitionRunningRef.current = false;
      setIsListening(false);
      clearSilenceTracking();
    }
  };

  const abortListening = () => {
    stopRecognition();
    speechToTextRef.current = '';
  };

  const resetAiConversation = () => {
    abortListening();
    conversationIdRef.current = '';
    parentMessageIdRef.current = '';
    paragraphsRef.current = [];
    setParagraphs([]);
  };

  const handleDeviceComboSelect = (combo) => {
    const previousCombo = confirmedDeviceComboRef.current;
    const isComboChange = Boolean(previousCombo && previousCombo !== combo);

    if (isComboChange) {
      resetAiConversation();
    }

    setConfirmedDeviceCombo(combo);
    setIsEditingDeviceCombo(false);
    if (sessionStorageKey) {
      try {
        sessionStorage.setItem(sessionStorageKey, combo);
      } catch {
        // ignore quota / private mode
      }
    }
  };

  const showDeviceComboPicker = !confirmedDeviceCombo || isEditingDeviceCombo;
  const showDeviceComboSummary = Boolean(confirmedDeviceCombo && !isEditingDeviceCombo);
  const canUseAiMic = Boolean(sessionType && confirmedDeviceCombo && !disable);

  const renderers = {
    image: ({ src, alt, title }) => (
      <img
        src={src}
        alt={alt}
        title={title}
        style={{ maxWidth: '100%', cursor: 'zoom-in', margin: '10px 0' }}
        onClick={() => setZoomImage(src)}
      />
    ),
  };

  const handleDevices = useCallback((mediaDevices) => {
    const videoDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput');
    setDevices(videoDevices);
    setHasCamera(videoDevices.length > 0);

    if (videoDevices.length > 0) {
      setActiveDeviceId(videoDevices[0].deviceId);
    } else {
      setError(I18n.t('no_cam'));
    }
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(handleDevices)
      .catch((err) => {
        console.log(err);
        setError(I18n.t('no_cam'));
        setHasCamera(false);
      });
  }, [handleDevices]);

  const switchCamera = (deviceId) => {
    setActiveDeviceId(deviceId);
  };

  const videoConstraints = {
    deviceId: activeDeviceId ? { exact: activeDeviceId } : undefined,
    facingMode: activeDeviceId ? undefined : 'user',
  };

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setContent(I18n.t('support_speech'));
      setDisable(true);
    }
  }, []);

  const resetSilenceTimeout = () => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }

    silenceTimeoutRef.current = setTimeout(async () => {
      console.log('⏱ Tự động dừng sau 3s im lặng');
      await stopListening();
    }, SILENCE_TIMEOUT);
  };

  const startListening = () => {
    if (!recognitionRef.current || isRecognitionRunningRef.current) return;

    recognitionRef.current.start();
    isRecognitionRunningRef.current = true;
    setIsListening(true);
    resetSilenceTimeout();
  };

  const stopListening = async () => {
    stopRecognition();
    if (speechToTextRef.current) {
      const userName = user.name;
      const deviceCombo = confirmedDeviceComboRef.current;
      const query = speechToTextRef.current;
      speechToTextRef.current = '';
      if (!sessionType || !deviceCombo) {
        return;
      }
      const dataAi = await sendToAiSupportSession(
        query,
        userName,
        { sessionType, deviceCombo },
        conversationIdRef.current,
        parentMessageIdRef.current,
      );
      if (dataAi) {
        conversationIdRef.current = dataAi.conversation_id;
        parentMessageIdRef.current = dataAi.message_id;
        setParagraphs([...paragraphsRef.current, dataAi.answer]);
        paragraphsRef.current = [...paragraphsRef.current, dataAi.answer];
      }
    }
  };

  const toggleListening = async () => {
    if (!canUseAiMic) {
      return;
    }

    if (isRecognitionRunningRef.current) {
      await stopListening();
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      if (!recognitionRef.current) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = I18n.language === 'vi' ? 'vi-VN' : 'en-US';

        recognition.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }
          if (finalTranscript || interimTranscript) {
            speechToTextRef.current = finalTranscript || interimTranscript;
            resetSilenceTimeout();
          }
        };

        recognition.onerror = (event) => {
          console.error('Lỗi nhận dạng:', event.error);
          stopListening();
        };

        recognition.onend = () => {
          if (isRecognitionRunningRef.current) {
            recognition.start();
            resetSilenceTimeout();
          } else {
            clearSilenceTracking();
          }
        };

        recognitionRef.current = recognition;
      }

      startListening();
      setDisable(false);
    } catch (err) {
      console.error('Không có quyền micro:', err);
      setContent(I18n.t('microphone_permission_denied'));
      setDisable(true);
    }
  };

  // Chỉ cho phép join khi đến giờ (phiên đang diễn ra). Tắt khi phiên đã kết thúc.
  const isEnded = sessionInfo?.state === 'is_ended' || sessionInfo?.infoExtra?.state === 'is_ended';
  const canJoin =
    !isEnded &&
    (sessionInfo?.state === 'is_processing' || sessionInfo?.infoExtra?.state === 'is_processing');

  const honorificName = user?.type === 'senior'
    ? I18n.t('senior_honorific')
    : I18n.t('buddy_honorific');

  return (
    <div className="container ai-support-session">
      <img
        className="back"
        src={arrowBackIcon}
        alt="Arrow Back"
        onClick={() => goBack()}
      />
      <div className="body">
        <div className="d-flex w-100 prejoin-layout">
          <div className="left">
            <div className="webcam-container">
              {error && (
                <div className="ml-3 error-message">
                  <p>{error}</p>
                </div>
              )}

              {!hasCamera && !error && (
                <div className="ml-3 no-camera-message">
                  <p>{I18n.t('check_camera')}</p>
                </div>
              )}

              {hasCamera && (
                <>
                  {devices.length > 1 && (
                    <div className="camera-selector">
                      <select
                        onChange={(e) => switchCamera(e.target.value)}
                        value={activeDeviceId}
                      >
                        {devices.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || I18n.t('camera_n', { n: index + 1 })}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <Webcam
                    className="prejoin-webcam"
                    style={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                    }}
                    audio={false}
                    ref={webcamRef}
                    videoConstraints={videoConstraints}
                    onUserMediaError={(err) => {
                      console.log(err);
                      setError(I18n.t('no_cam'));
                      setHasCamera(false);
                    }}
                    mirrored={true}
                  />
                </>
              )}
            </div>
          </div>
          <div className="right">
            <div className="prejoin-right__header">
              <span className="title">{I18n.t('help_setting_device')}</span>

              {sessionType ? (
                <div className="prejoin-device-combo">
                  {showDeviceComboPicker ? (
                    <>
                      <p className="prejoin-device-combo__label">{I18n.t('prejoin_device_combo_label')}</p>
                      <div className="prejoin-device-combo__options">
                        {MVP_DEVICE_COMBOS.map((combo) => {
                          const isSelected = confirmedDeviceCombo === combo;
                          return (
                            <button
                              key={combo}
                              type="button"
                              className={[
                                'prejoin-device-combo__option',
                                isSelected ? 'prejoin-device-combo__option--selected' : '',
                              ].filter(Boolean).join(' ')}
                              onClick={() => handleDeviceComboSelect(combo)}
                            >
                              {I18n.t(MVP_DEVICE_COMBO_I18N_KEYS[combo])}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : null}
                  {showDeviceComboSummary ? (
                    <div className="prejoin-device-combo__selected">
                      <span className="prejoin-device-combo__selected-text">
                        {I18n.t('prejoin_device_combo_selected', {
                          device: I18n.t(MVP_DEVICE_COMBO_I18N_KEYS[confirmedDeviceCombo]),
                        })}
                      </span>
                      <button
                        type="button"
                        className="prejoin-device-combo__change"
                        onClick={() => setIsEditingDeviceCombo(true)}
                      >
                        {I18n.t('prejoin_device_combo_change')}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="prejoin-session-type prejoin-session-type--missing">
                  {I18n.t('prejoin_session_type_missing')}
                </p>
              )}

              {sessionType && confirmedDeviceCombo && !paragraphs.length ? (!content ?
                <span className="prejoin-prompt">{I18n.t('difficulty_facing', { name: honorificName })}</span>
                :
                <span className="prejoin-prompt">{content}</span>)
                : null
              }
            </div>

            {sessionType && paragraphs.length > 0 && (
              <div className="prejoin-right__chat">
                <div className="content">
                  {paragraphs.map((text, index) => (
                    <ReactMarkdown key={index} renderers={renderers} linkTarget="_blank">
                      {text}
                    </ReactMarkdown>
                  ))}
                </div>
              </div>
            )}

            {sessionType && (
              !isListening ?
                <div
                  className={`mic${canUseAiMic ? '' : ' mic--disabled'}`}
                  onClick={toggleListening}
                >
                  <div className={canUseAiMic ? '' : 'in-active'}>
                    <img src={micphone} alt="micphone" />
                  </div>
                  <span>{I18n.t('press_to_talk')}</span>
                </div>
                :
                <div className="mic" onClick={toggleListening}>
                  <div className="position-relative">
                    <div className="pulse-ring"></div>
                    <div className="pulse-ring"></div>
                    <div className="pulse-ring"></div>
                    <div className="mic-icon"><img src={micphone} alt="micphone" /></div>
                  </div>
                  <span>{I18n.t('press_to_talk')}</span>
                </div>
            )}
          </div>
        </div>
        <button
          onClick={() => canJoin && joinSession()}
          className="start"
          disabled={!canJoin}
        >
          {I18n.t('join_session_sort_title')}
        </button>
      </div>
      {zoomImage && (
        <div className="image-popup" onClick={() => setZoomImage(null)}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <img src={zoomImage} alt="Zoom" />
            <button className="close-btn" onClick={() => setZoomImage(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      sendToAiSupportSession,
      getDetailSession,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AiSupportSessionVideo);
